import { useState, useRef, useEffect } from 'react';

/**
 * Reusable 6-digit OTP input component with auto-focus and paste support.
 */
export default function OTPInput({ length = 6, value = '', onChange, disabled = false }) {
  const [digits, setDigits] = useState(Array(length).fill(''));
  const inputRefs = useRef([]);

  useEffect(() => {
    if (value) {
      const arr = value.split('').slice(0, length);
      setDigits([...arr, ...Array(length - arr.length).fill('')]);
    }
  }, [value, length]);

  const handleChange = (index, val) => {
    const digit = val.replace(/\D/g, '').slice(-1);
    const newDigits = [...digits];
    newDigits[index] = digit;
    setDigits(newDigits);
    onChange(newDigits.join(''));

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (pasted) {
      const newDigits = pasted.split('').concat(Array(length).fill('')).slice(0, length);
      setDigits(newDigits);
      onChange(newDigits.join(''));
      const nextEmpty = newDigits.findIndex(d => !d);
      inputRefs.current[nextEmpty >= 0 ? nextEmpty : length - 1]?.focus();
    }
  };

  return (
    <div className="flex gap-2 justify-center" data-testid="otp-input-group">
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={el => (inputRefs.current[i] = el)}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={digit}
          onChange={e => handleChange(i, e.target.value)}
          onKeyDown={e => handleKeyDown(i, e)}
          onPaste={i === 0 ? handlePaste : undefined}
          disabled={disabled}
          className="w-12 h-14 text-center text-xl font-mono bg-white/5 border border-white/10 text-white
                     focus:outline-none focus:border-[#4ADE80]/50 disabled:opacity-40 transition-colors"
          data-testid={`otp-digit-${i}`}
        />
      ))}
    </div>
  );
}
